import { useEffect, useState } from "react";
import { apiPost, tryApiGet } from "../api/client";
import { SectionCard } from "./SectionCard";
import type { ReconciliationLink } from "../api/types";
import { relativeTime } from "../lib/format";

// Reconciliation evidence (phase 2 brief, item 4). Real, exact endpoints —
// confirmed from the backend's own source by the coordinator, 2026-09-20
// (the routes were still landing in /openapi.json at the time, so this
// deliberately does NOT path-guess anymore, per that direction):
//
//   GET  /api/reconciliation/nodes/{protocol}/{native_id}    -- viewer; every
//        link touching this node, any status
//   POST /api/reconciliation/candidates/{link_id}/confirm    -- admin
//   POST /api/reconciliation/candidates/{link_id}/reject     -- admin
//   POST /api/reconciliation/links/{link_id}/revoke          -- admin;
//        reverses an already-confirmed link (reconciliation is corrective,
//        never destructive, per the design doc)
//
// Every route is a raw `SELECT *` (or an UPDATE) against node_identity_links
// (backend/amalgamesh/api/routes/reconciliation.py; real columns per
// db/migrations/0005_phase2_core.sql: id, node_a_protocol, node_a_native_id,
// node_b_protocol, node_b_native_id, score, evidence_json, status, active,
// created_at, decided_at, decided_by, revoked_at, revoked_by) — there is no
// per-link "observer" or "canonical_id" field; a link is node-to-node
// evidence produced by the reconciliation scan, not a per-observer
// submission (2026-09-23 fix: an earlier version of this component read
// candidate_observer_id/observer_id/canonical_id/evidence, none of which
// this table has, so the evidence blob, the other side of the link, and
// the whole decision/revocation trail were fetched and silently dropped).
//
// A link's exact status vocabulary isn't pinned by a schema (the list route
// returns whatever the backend's own model serializes), so this recognizes
// a small set of reasonable spellings for "still a candidate" vs "already
// merged" to decide which action applies, defaulting an unrecognized status
// to "pending" (the confirm/reject action, the safer default: it stays
// reversible and requires an explicit admin decision either way — treating
// an unknown status as unconfirmable would risk resurfacing a merge nobody
// can now act on).
const CONFIRMED_STATUSES = new Set(["confirmed", "approved", "active", "merged"]);
const REJECTED_STATUSES = new Set(["rejected", "denied"]);
const REVOKED_STATUSES = new Set(["revoked"]);

interface LinkSide {
  protocol: string;
  native_id: string;
}

function sideMatches(side: LinkSide, protocol: string | undefined, nodeId: string): boolean {
  return !!protocol && side.protocol === protocol && side.native_id === nodeId;
}

// Parses evidence_json defensively (design constraint: malformed evidence
// must never crash this section) — falls back to the raw string when it
// won't parse.
function parseEvidence(raw: string): { parsed: unknown; ok: boolean } {
  try {
    return { parsed: JSON.parse(raw), ok: true };
  } catch {
    return { parsed: null, ok: false };
  }
}

export function ReconciliationSection({ nodeId, protocol, isAdmin }: { nodeId: string; protocol: string | undefined; isAdmin: boolean }) {
  const [links, setLinks] = useState<ReconciliationLink[] | null | "unavailable">(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!protocol) return;
    let cancelled = false;
    setLinks(null);
    tryApiGet<ReconciliationLink[]>(`/api/reconciliation/nodes/${encodeURIComponent(protocol)}/${encodeURIComponent(nodeId)}`)
      .then((res) => !cancelled && setLinks(res ?? "unavailable"))
      .catch(() => !cancelled && setLinks("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [nodeId, protocol]);

  function linkId(link: ReconciliationLink): string {
    return String(link.id);
  }

  async function act(link: ReconciliationLink, action: "confirm" | "reject" | "revoke") {
    const id = linkId(link);
    setBusyId(id);
    setActionError(null);
    const path =
      action === "revoke" ? `/api/reconciliation/links/${encodeURIComponent(id)}/revoke` : `/api/reconciliation/candidates/${encodeURIComponent(id)}/${action}`;
    try {
      // The confirm/reject/revoke routes return {"ok": true}, not the
      // updated row (see reconciliation.py) — so the new status is applied
      // optimistically here, same as before this fix.
      await apiPost<{ ok: boolean }>(path);
      setLinks((prev) =>
        Array.isArray(prev)
          ? prev.map((l) =>
              linkId(l) === id
                ? {
                    ...l,
                    status: action === "revoke" ? l.status : action === "confirm" ? "confirmed" : "rejected",
                    active: action === "confirm" ? 1 : 0,
                  }
                : l,
            )
          : prev,
      );
    } catch (e) {
      setActionError(e instanceof Error ? e.message : `Couldn't ${action} this link.`);
    } finally {
      setBusyId(null);
    }
  }

  // 2026-09-23 reorder/Commands pass (Change 1): this section now decides
  // for itself whether it's worth a full card. "No links — nothing to
  // merge" is the routine case (most nodes have none) — that and the
  // brief loading window before the first response render NOTHING at all,
  // same convention MeshMonitor uses for an empty card.
  //
  // 2026-09-26 operator-review fix: "unavailable" used to render a card
  // with developer-facing prose about backend build gaps and route
  // deployment timing -- not something an operator can act on. The
  // feature itself is unchanged; when its data isn't available, the whole
  // section is simply omitted, same as the "no links" case below.
  if (!protocol || links === null || links === "unavailable") return null;

  if (links.length === 0) return null;

  return (
    <SectionCard title="Reconciliation evidence" description="Cross-protocol identity links proposed for this node, and their evidence.">
      <div className="node-detail-note" style={{ marginBottom: 10 }}>
        Reconciliation is corrective, never destructive: a link below is a candidate merge, not something already
        applied. Auto-merge is off — every link stays <strong>pending</strong> until an admin confirms it, and a
        confirmed link can still be revoked.
      </div>
      <ul className="reconciliation-list">
        {links.map((link, i) => {
          const status = (link.status ?? "pending").toLowerCase();
          const confirmed = CONFIRMED_STATUSES.has(status);
          const rejected = REJECTED_STATUSES.has(status) || REVOKED_STATUSES.has(status);
          const pending = !confirmed && !rejected;
          const id = linkId(link);

          const aIsThis = sideMatches({ protocol: link.node_a_protocol, native_id: link.node_a_native_id }, protocol, nodeId);
          const bIsThis = sideMatches({ protocol: link.node_b_protocol, native_id: link.node_b_native_id }, protocol, nodeId);
          const other: LinkSide = aIsThis
            ? { protocol: link.node_b_protocol, native_id: link.node_b_native_id }
            : { protocol: link.node_a_protocol, native_id: link.node_a_native_id };
          // Neither side matching (shouldn't happen given this endpoint is
          // queried by (protocol, native_id) as one of the two sides) — show
          // both plainly rather than mislabeling one as "this node".
          const neitherMatched = !aIsThis && !bIsThis;

          const evidence = parseEvidence(link.evidence_json);

          return (
            <li key={id ?? i} className="reconciliation-row">
              <div className="reconciliation-row-head">
                <span
                  className={`badge reconciliation-status-${confirmed ? "approved" : rejected ? "rejected" : "pending"}`}
                >
                  {status}
                </span>
                <span className="reconciliation-score">score {Number(link.score).toFixed(2)}</span>
                <span className="reconciliation-score">{link.active ? "active" : "inactive"}</span>
              </div>

              <div className="reconciliation-row-head">
                {neitherMatched ? (
                  <>
                    <span className="reconciliation-score">
                      {link.node_a_protocol}/{link.node_a_native_id}
                    </span>
                    <span className="reconciliation-score">
                      {link.node_b_protocol}/{link.node_b_native_id}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="reconciliation-score">this node: {protocol}/{nodeId}</span>
                    <span className="reconciliation-score">
                      other node: {other.protocol}/{other.native_id}
                    </span>
                  </>
                )}
              </div>

              {(link.decided_at || link.decided_by || link.revoked_at || link.revoked_by) && (
                <div className="reconciliation-row-head">
                  {link.decided_at && (
                    <span className="reconciliation-score">decided {relativeTime(link.decided_at)}{link.decided_by ? ` by ${link.decided_by}` : ""}</span>
                  )}
                  {link.revoked_at && (
                    <span className="reconciliation-score">revoked {relativeTime(link.revoked_at)}{link.revoked_by ? ` by ${link.revoked_by}` : ""}</span>
                  )}
                </div>
              )}

              <pre className="node-detail-json">{evidence.ok ? JSON.stringify(evidence.parsed, null, 2) : link.evidence_json}</pre>

              {isAdmin && id && pending && (
                <div className="reconciliation-actions">
                  <button disabled={busyId === id} onClick={() => act(link, "confirm")}>
                    Confirm merge
                  </button>
                  <button disabled={busyId === id} onClick={() => act(link, "reject")}>
                    Reject
                  </button>
                </div>
              )}
              {isAdmin && id && confirmed && (
                <div className="reconciliation-actions">
                  <button disabled={busyId === id} onClick={() => act(link, "revoke")}>
                    Revoke merge
                  </button>
                </div>
              )}
              {!isAdmin && pending && <div className="node-detail-note">Only an admin can confirm or reject this link.</div>}
            </li>
          );
        })}
      </ul>
      {actionError && <div className="auth-error">{actionError}</div>}
    </SectionCard>
  );
}
